import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X, Eye, FileText, Check, XCircle, Search } from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './PromotionManage.module.css';
import usersStyles from './Users.module.css';

export default function ApprovalDashboard() {
  const { user, currentFunctionRole } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const role = currentFunctionRole || '';

  const [tab, setTab] = useState<'pending'|'reviewed'>('pending');
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [previewed, setPreviewed] = useState<Set<number>>(new Set());
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewFileId, setPreviewFileId] = useState<number|null>(null);
  const [showDetail, setShowDetail] = useState<any>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [logsTitle, setLogsTitle] = useState('');
  const [logsData, setLogsData] = useState<any[]>([]);
  const [logsStatus, setLogsStatus] = useState(0);
  const [showReject, setShowReject] = useState(false);
  const [rejectFileId, setRejectFileId] = useState(0);
  const [rejectTitle, setRejectTitle] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [showApprove, setShowApprove] = useState(false);
  const [approveFileId, setApproveFileId] = useState(0);
  const [approveTitle, setApproveTitle] = useState('');
  const [approveCategory, setApproveCategory] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const endpoint = tab === 'pending' ? '/promotions/approvals/pending' : '/promotions/approvals/reviewed';
      const res = await api.request<any[]>(endpoint);
      setFiles(res.data || []);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const filtered = (() => {
    let result = files.filter(f => {
      if (!searchQuery) return true;
      const t = (f.title || f.file_name || '').toLowerCase();
      const s = (f.submitter_name || f.promoter_name || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      return t.includes(q) || s.includes(q);
    });
    if (sortField) {
      result.sort((a:any,b:any) => {
        let va = a[sortField] ?? '', vb = b[sortField] ?? '';
        if (sortField === 'submit_time' || sortField === 'review_time' || sortField === 'create_time') {
          va = va || ''; vb = vb || '';
        }
        const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  })();

  const openPreview = (f: any) => { setPreviewUrl(f.preview_url || ''); setPreviewType(f.file_type || ''); setPreviewTitle(f.title || f.file_name); setPreviewFileId(f.file_id); };
  const closePreview = () => { if (previewFileId !== null) setPreviewed(p => new Set(p).add(previewFileId)); setPreviewUrl(''); setPreviewFileId(null); };

  const openLogs = async (f: any) => {
    setLogsTitle(f.title || f.file_name);
    setLogsStatus(f.approval_status);
    setShowLogs(true);
    try { const res = await api.request<any[]>('/promotions/files/' + f.file_id + '/approval-logs'); setLogsData(res.data || []); }
    catch { setLogsData([]); }
  };

  const handleApprove = async (f: any) => {
    if (role === '研发') {
      setApproveFileId(f.file_id); setApproveTitle(f.title || f.file_name); setApproveCategory(''); setShowApprove(true);
    } else {
      const ok = await confirmDialog.confirm({ title: '确认通过', message: '确认通过「' + (f.title || f.file_name) + '」？', confirmText: '确认通过' });
      if (!ok) return;
      await doApprove(f.file_id, null);
    }
  };

  const doApprove = async (fid: number, category: string|null) => {
    setProcessing(true);
    try { await api.request('/promotions/files/' + fid + '/approve', { method: 'POST', body: JSON.stringify({ action: '通过', category_suggestion: category }) }); showToast('success', '已通过'); setShowApprove(false); loadData(); window.dispatchEvent(new CustomEvent('refresh-sidebar-counts')); }
    catch (err: any) { showToast('error', getErrorMessage(err)); }
    finally { setProcessing(false); }
  };

  const handleRejectConfirm = async () => {
    if (!rejectComment.trim()) { showToast('warning', '请填写驳回理由'); return; }
    setProcessing(true);
    try { await api.request('/promotions/files/' + rejectFileId + '/reject', { method: 'POST', body: JSON.stringify({ action: '驳回', comment: rejectComment }) }); showToast('success', '已驳回'); setShowReject(false); setRejectComment(''); loadData(); window.dispatchEvent(new CustomEvent('refresh-sidebar-counts')); }
    catch (err: any) { showToast('error', getErrorMessage(err)); }
    finally { setProcessing(false); }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortTh = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <th className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  const formatDate = (d: string) => d ? new Date(d).toLocaleString('zh-CN') : '-';

  return (
    <div className={usersStyles.container}>
      <div className={usersStyles.header} style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <h1 className={usersStyles.title}>视频审核</h1>
        <div style={{display:'flex',border:'1px solid var(--ink-200)',borderRadius:10,overflow:'hidden'}}>
          <button onClick={()=>setTab('pending')} style={{padding:'10px 24px',border:'none',cursor:'pointer',fontSize:'.9375rem',fontWeight:600,background:tab==='pending'?'var(--cinnabar-500)':'#fff',color:tab==='pending'?'#fff':'var(--ink-500)'}}>待审核</button>
          <button onClick={()=>setTab('reviewed')} style={{padding:'10px 24px',border:'none',cursor:'pointer',fontSize:'.9375rem',fontWeight:600,background:tab==='reviewed'?'var(--cinnabar-500)':'#fff',color:tab==='reviewed'?'#fff':'var(--ink-500)'}}>审核记录</button>
        </div>
      </div>
      <div className={usersStyles.searchBar}>
        <div className={usersStyles.searchInput}>
          <Search size={18} className={usersStyles.searchIcon} strokeWidth={1.5} />
          <input type="text" placeholder="搜索视频名称或供应商..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} />
          {searchQuery && <button className={usersStyles.clearSearch} onClick={()=>setSearchQuery('')}><X size={16}/></button>}
        </div>
      </div>
      <div style={{flex:1,overflow:'auto'}}>
        {loading ? <div style={{textAlign:'center',padding:60}}><Loader2 size={24} className={styles.spin}/></div> :
        filtered.length===0 ? <div style={{textAlign:'center',padding:60,color:'var(--ink-400)'}}>{searchQuery?'没有匹配的视频':(tab==='pending'?'暂无待审核视频':'暂无审核记录')}</div> : (
          tab==='pending' ? (
            /* 待审核 — 卡片布局 */
            <div className={styles.grid} style={{padding:'0 0 20px'}}>
              {filtered.map((f:any)=>(
                <motion.div key={f.file_id} className={styles.card} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
                  <div className={styles.cardCover} style={{cursor:'pointer'}} onClick={()=>openPreview(f)}>
                    {['mp4','webm','mov','avi'].includes((f.file_type||'').toLowerCase())
                      ? <video src={(f.preview_url||'').replace('/minio/','/internal-minio/')} muted loop playsInline className={styles.coverVideo} />
                      : ['jpg','jpeg','png','gif'].includes((f.file_type||'').toLowerCase())
                      ? <img src={(f.preview_url||'').replace('/minio/','/internal-minio/')} className={styles.coverImg} />
                      : <div className={styles.coverEmpty} style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--ink-300)'}}>点击预览</div>}
                  </div>
                  <div className={styles.cardBody}>
                    <h3 style={{fontSize:'.9375rem',fontWeight:700,textAlign:'center',margin:0}}>{f.title||f.file_name}</h3>
                    <div style={{fontSize:'.75rem',color:'var(--ink-500)',textAlign:'center',marginTop:4}}>{(f.submitter_name||f.promoter_name||'未知供应商')}</div>
                  </div>
                  <div className={styles.cardActions}>
                    <button onClick={(e)=>{e.stopPropagation(); openLogs(f);}}>
                      <FileText size={15}/> 流程
                    </button>
                    <button onClick={(e)=>{e.stopPropagation(); setShowDetail({title:f.title||f.file_name,desc:f.description,submitter:f.submitter_name||f.promoter_name,time:f.create_time});}}>
                      <Eye size={15}/> 详情
                    </button>
                    <button onClick={(e)=>{e.stopPropagation(); handleApprove(f);}} disabled={!previewed.has(f.file_id)||processing}
                      style={{opacity:previewed.has(f.file_id)?1:0.4,color:previewed.has(f.file_id)?'#16a34a':undefined}}>
                      <Check size={15}/> 通过
                    </button>
                    <button onClick={(e)=>{e.stopPropagation(); setRejectFileId(f.file_id);setRejectTitle(f.title||f.file_name);setShowReject(true);}} disabled={!previewed.has(f.file_id)||processing}
                      style={{opacity:previewed.has(f.file_id)?1:0.4,color:previewed.has(f.file_id)?'#dc2626':undefined}}>
                      <XCircle size={15}/> 驳回
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            /* 审核记录 — 表格布局 */
            <div className={usersStyles.tableContainer}>
              <table className={usersStyles.table} style={{tableLayout:'fixed'}}>
                <thead><tr>
                  <SortTh field="title">视频名称</SortTh>
                  <SortTh field="submitter_name">送审人员</SortTh>
                  <SortTh field="submit_time">送审时间</SortTh>
                  <SortTh field="review_result">审核结果</SortTh>
                  <th className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{width:'12%'}}>驳回理由</th>
                  <SortTh field="reviewer_name">审核人员</SortTh>
                  <SortTh field="review_time">审核时间</SortTh>
                  <th className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`}>操作</th>
                </tr></thead>
                <tbody>
                  {filtered.map((f:any)=>(
                    <motion.tr key={tab+'-'+(f.id||f.file_id)} initial={{opacity:0}} animate={{opacity:1}}>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`}><span style={{fontWeight:500}}>{f.title||f.file_name}</span></td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{f.submitter_name||f.promoter_name||'-'}</td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{f.submit_time?formatDate(f.submit_time):'-'}</td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`}><span style={{padding:'2px 8px',borderRadius:4,background:f.review_result==='通过'?'#dcfce7':'#fee2e2',color:f.review_result==='通过'?'#16a34a':'#dc2626',fontSize:'.75rem',fontWeight:500}}>{f.review_result||'-'}</span></td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{fontSize:'.75rem',color:'var(--ink-500)',whiteSpace:'normal',wordBreak:'break-word'}}>{f.reject_reason||'-'}</td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{f.reviewer_name||'-'}</td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{f.review_time?formatDate(f.review_time):'-'}</td>
                      <td className={`${usersStyles.tableHeaderCell} ${usersStyles.centerCell}`}>
                        <div className={usersStyles.tableActions} style={{justifyContent:'center'}}>
                          <button className={usersStyles.tableActionBtn} onClick={(e)=>{e.stopPropagation(); openLogs(f);}} title="审核流程"><FileText size={14}/></button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <AnimatePresence>{previewUrl && <motion.div key="preview" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={closePreview}>
        <motion.div style={{maxWidth:'90vw',maxHeight:'90vh',background:'#000',borderRadius:12,overflow:'hidden'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 20px',background:'#1a1a1a',color:'#fff'}}><span>{previewTitle}</span><span style={{fontSize:'.75rem',color:'#9ca3af'}}>关闭预览激活通过/驳回</span><button onClick={closePreview} style={{background:'none',border:'none',color:'#fff',cursor:'pointer'}}><X size={20}/></button></div>
          {['mp4','webm','mov','avi'].includes(previewType.toLowerCase()) ? <video src={previewUrl.replace('/minio/','/internal-minio/')} controls autoPlay playsInline controlsList="nodownload" onContextMenu={e=>e.preventDefault()} style={{display:'block',maxWidth:'100%',maxHeight:'80vh',margin:'0 auto'}}/> : <img src={previewUrl.replace('/minio/','/internal-minio/')} style={{display:'block',maxWidth:'100%',maxHeight:'80vh',margin:'0 auto'}}/>}
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* Video Detail Modal */}
      <AnimatePresence>{showDetail && <motion.div key="detail" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowDetail(null)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
          <div className={styles.modalHead}><h2>视频详情 · {showDetail.title}</h2><button onClick={()=>setShowDetail(null)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <div style={{marginBottom:12}}><label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>视频描述</label><p style={{fontSize:'.875rem',marginTop:4}}>{showDetail.desc||'无'}</p></div>
            <div style={{marginBottom:12}}><label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>送审人员</label><p style={{fontSize:'.875rem',marginTop:4}}>{showDetail.submitter||'-'}</p></div>
            <div style={{marginBottom:12}}><label style={{fontSize:'.75rem',color:'var(--ink-400)'}}>送审时间</label><p style={{fontSize:'.875rem',marginTop:4}}>{showDetail.time?formatDate(showDetail.time):'-'}</p></div>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showLogs && <motion.div key="logs" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowLogs(false)}>
        <motion.div className={styles.modalLg} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:600}}>
          <div className={styles.modalHead}><h2>审核记录 · {logsTitle}</h2><button onClick={()=>setShowLogs(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <table style={{width:'100%',fontSize:'.8125rem',borderCollapse:'collapse'}}>
              <thead><tr style={{borderBottom:'2px solid var(--ink-100)'}}><th style={{textAlign:'center',padding:'8px 4px'}}>人物</th><th style={{textAlign:'center',padding:'8px 4px'}}>动作</th><th style={{textAlign:'center',padding:'8px 4px'}}>备注</th><th style={{textAlign:'center',padding:'8px 4px'}}>时间</th></tr></thead>
              <tbody>
                {(()=>{
                  const steps=[{role:'采购',label:'规范性审核',s:1},{role:'研发',label:'技术性审核',s:2}];
                  const rows:any[]=[];
                  const logs=[...logsData].sort((a,b)=>new Date(a.create_time).getTime()-new Date(b.create_time).getTime());
                  logs.forEach((l:any)=>{
                    if(l.action==='提交审核'){rows.push({name:l.approver_name,action:'送审',comment:'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:'#16a34a'});return}
                    const label=steps.find(s=>s.role===l.approval_role)?.label||l.approval_role;
                    if(l.action==='撤回'){rows.push({name:l.approver_name,action:'撤回审核',comment:'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:'#f59e0b'});return}
                    if(l.action==='通过'){rows.push({name:l.approver_name,action:label+'完成',comment:'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:'#16a34a'});return}
                    if(l.action==='驳回'){rows.push({name:l.approver_name,action:label+'驳回',comment:l.comment||'',time:new Date(l.create_time).toLocaleString('zh-CN'),color:'#dc2626'});return}
                  });
                  if([1,2,3].includes(logsStatus)){steps.forEach(s=>{if(s.s>logsStatus)rows.push({name:'',action:s.label+'未开始',comment:'',time:'',color:'var(--ink-300)'});else if(s.s===logsStatus)rows.push({name:'',action:s.label+'中',comment:'',time:'',color:'#2563eb'});})}
                  if(logsStatus===4)rows.push({name:'',action:'已上线',comment:'',time:'',color:'#16a34a'});
                  return rows.map((r,i)=><tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}><td style={{padding:'6px 4px',textAlign:'center'}}>{r.name||'-'}</td><td style={{padding:'6px 4px',color:r.color,fontWeight:500,textAlign:'center'}}>{r.action}</td><td style={{padding:'6px 4px',color:'var(--ink-400)',textAlign:'center'}}>{r.comment||'-'}</td><td style={{padding:'6px 4px',color:'var(--ink-400)',textAlign:'center'}}>{r.time||'-'}</td></tr>);
                })()}
              </tbody>
            </table>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      <AnimatePresence>{showApprove && <motion.div key="approve" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowApprove(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:380}}>
          <div className={styles.modalHead}><h2>审核通过 · {approveTitle}</h2><button onClick={()=>setShowApprove(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <label style={{fontSize:'.875rem',fontWeight:500,marginBottom:8,display:'block'}}>视频分类 *</label>
            <select value={approveCategory} onChange={e=>setApproveCategory(e.target.value)}
              style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid var(--ink-200)',fontSize:'.875rem'}}>
              <option value="">请选择分类</option>
              {['基材类','油墨涂料类','印刷工艺类','功能类','智能化类','绿色可持续类'].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button onClick={()=>setShowApprove(false)} style={{padding:'8px 20px',borderRadius:8,border:'1px solid var(--ink-200)',background:'#fff',cursor:'pointer',fontSize:'.875rem'}}>取消</button>
              <button onClick={()=>{ if(!approveCategory){showToast('warning','请选择视频分类');return;} doApprove(approveFileId, approveCategory); }} disabled={processing}
                style={{padding:'8px 20px',borderRadius:8,border:'none',background:'#16a34a',color:'#fff',cursor:'pointer',fontSize:'.875rem',opacity:processing?0.5:1}}>
                {processing?'提交中...':'确认通过'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>
      <AnimatePresence>{showReject && <motion.div key="reject" className={styles.overlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowReject(false)}>
        <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()} style={{maxWidth:420}}>
          <div className={styles.modalHead}><h2>驳回 · {rejectTitle}</h2><button onClick={()=>setShowReject(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}><label style={{fontSize:'.875rem',fontWeight:500,marginBottom:8,display:'block'}}>驳回理由 *</label><textarea value={rejectComment} onChange={e=>setRejectComment(e.target.value)} rows={3} placeholder="请填写驳回理由" style={{width:'100%',padding:'10px 12px',borderRadius:8,border:'1px solid var(--ink-200)',fontSize:'.875rem',resize:'vertical'}}/>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}><button onClick={()=>setShowReject(false)} style={{padding:'8px 20px',borderRadius:8,border:'1px solid var(--ink-200)',background:'#fff',cursor:'pointer',fontSize:'.875rem'}}>取消</button><button onClick={handleRejectConfirm} disabled={processing} style={{padding:'8px 20px',borderRadius:8,border:'none',background:'#dc2626',color:'#fff',cursor:'pointer',fontSize:'.875rem',opacity:processing?.5:1}}>{processing?'提交中...':'确认驳回'}</button></div></div>
        </motion.div>
      </motion.div>}</AnimatePresence>
    </div>
  );
}
